import { useState } from 'react';

export type SortDirectionType = 'asc' | 'desc';

export type SortStateType<TKey extends string> = {
  key: TKey;
  direction: SortDirectionType;
  toggle: (key: TKey) => void;
};

/**
 * Which column a table is ordered by, and which way.
 *
 * `onChange` exists so a page can send the reader back to the first page. A sort that keeps you on page 4
 * shows you rows you did not ask for, and the row you were looking at is somewhere else entirely.
 */
export const useSort = <TKey extends string>(
  initialKey: TKey,
  initialDirection: SortDirectionType = 'asc',
  onChange?: () => void,
): SortStateType<TKey> => {
  const [key, setKey] = useState<TKey>(initialKey);
  const [direction, setDirection] = useState<SortDirectionType>(initialDirection);

  return {
    key,
    direction,
    toggle: (next: TKey) => {
      setDirection(key === next && direction === 'asc' ? 'desc' : 'asc');
      setKey(next);
      onChange?.();
    },
  };
};

/**
 * Orders rows in the browser, for the tables that already hold their whole list.
 *
 * `localeCompare` with `sk`, not `<`: code-point order puts Č after Z and Š after S, so „Čítanie" would
 * sort to the end of a Slovak column and look like a bug to everyone who reads it.
 *
 * A table paginated by the server must **not** use this — ordering the twenty-five rows on screen and
 * calling it sorted is a worse lie than no sorting at all, because it looks like it worked.
 */
export const sortRows = <TRow>(
  rows: ReadonlyArray<TRow>,
  direction: SortDirectionType,
  valueOf: (row: TRow) => string | number | null,
): Array<TRow> => {
  const sign: number = direction === 'asc' ? 1 : -1;

  return [...rows].sort((left: TRow, right: TRow): number => {
    const a: string | number | null = valueOf(left);
    const b: string | number | null = valueOf(right);

    // Nothing is always last, whichever way the column is pointing. A row with no value is not the
    // smallest row, it is a row with no value, and burying it at the top of a descending sort hides it.
    if (a === null && b === null) {
      return 0;
    }

    if (a === null) {
      return 1;
    }

    if (b === null) {
      return -1;
    }

    if (typeof a === 'number' && typeof b === 'number') {
      return (a - b) * sign;
    }

    return String(a).localeCompare(String(b), 'sk') * sign;
  });
};
